import {
  Box,
  Container,
  Drawer,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { useIntl } from "react-intl";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import { useCustomThemeContext } from "globalProviders/CustomThemeProvider";
import React, { useState, useEffect } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import useFavicon from "hooks/useFavicon";
import { useQuery } from "react-query";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import LeftMenuItemList from "./LeftMenuItemList";
import {
  getTextColor,
  HEADER_HEIGHT,
  LEFT_NAV_WIDTH,
  MenuTabs,
  TopBar,
} from "@knowfully/ui.common";
import StartIcon from "@mui/icons-material/Start";
import ProfileMenu from "components/TopBar/ProfileMenu";

const getStyles = (
  theme: Theme,
  hideLeftMenu?: boolean
): { [key: string]: SxProps } => {
  const backgroundColor = theme.palette.background.default;
  const itemTextColor = getTextColor(theme, backgroundColor);
  const selectedItemBackgroundColor = theme.palette.primary.light;
  const selectedItemTextColor = getTextColor(
    theme,
    selectedItemBackgroundColor
  );

  return {
    container: {
      marginTop: HEADER_HEIGHT,
      py: 2,
      [theme.breakpoints.up("lg")]: {
        maxWidth: hideLeftMenu ? undefined : "initial",
      },
    },
    leftPanel: {
      zIndex: 1200,
      mt: HEADER_HEIGHT,
      pb: HEADER_HEIGHT,
      backgroundColor: theme.palette.background.default,
      width: LEFT_NAV_WIDTH,
      transition: "width 750ms ease",
      justifyContent: "space-between",
    },
    panelDrawer: {
      width: LEFT_NAV_WIDTH,
      transition: "width 750ms ease",
    },
    collapseMenu: {
      py: 1,
      cursor: "pointer",
      whiteSpace: "nowrap",
      color: itemTextColor,
      "& i": {
        color: itemTextColor,
      },
      "&:hover": {
        color: selectedItemTextColor,
        backgroundColor: selectedItemBackgroundColor,
        "& i": {
          color: selectedItemTextColor,
        },
      },
    },
  };
};

type LeftMenuLayoutProps = {
  hideLeftMenu?: boolean;
  children?: React.ReactNode;
};

const LeftMenuLayout = React.memo(
  ({ hideLeftMenu, children }: LeftMenuLayoutProps) => {
    const { formatMessage: t } = useIntl();
    const theme = useTheme();
    const styles = getStyles(theme, hideLeftMenu);
    const { logoImageUrl, logoAlternativeText, faviconUrl } =
      useCustomThemeContext();
    useFavicon(faviconUrl);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [collapsed, setCollapsed] = useState(isMobile);
    const {
      menuService,
      authenticationService,
      documentsUrl,
      coursesUrl,
      courseDetailsUrl,
      agreementReviewUrl,
    } = useGlobalContext();
    const location = useLocation();

    const { data: menu } = useQuery(
      ["getLeftMenu"],
      () => menuService.getLeftMenu(),
      {
        staleTime: Infinity,
      }
    );

    useEffect(() => {
      if (isMobile) {
        setCollapsed(true);
      }
    }, [isMobile]);

    const menuItems = menu?.menuItems ?? [];
    const leftMenuItems = menuItems.filter((i) => !i.parentId);
    const tabItems = menuItems.filter((i) => !!i.parentId);

    const subRouteToParentMenu: { route: string; menuUrl: string }[] = [
      { route: courseDetailsUrl(), menuUrl: coursesUrl() },
      { route: agreementReviewUrl(), menuUrl: documentsUrl() },
    ];

    const mappedParentMenuUrl = subRouteToParentMenu.find((m) =>
      matchPath(
        { path: m.route, caseSensitive: false, end: true },
        location.pathname
      )
    )?.menuUrl;

    const currentMenuItem =
      menuItems.find(
        (mi) =>
          !!matchPath(
            { path: mi.url, caseSensitive: false, end: true },
            location.pathname
          )
      ) ??
      (mappedParentMenuUrl
        ? menuItems.find((mi) => mi.url === mappedParentMenuUrl)
        : undefined);

    const currentTabs = currentMenuItem
      ? tabItems
          .filter(
            (ti) =>
              ti.parentId === currentMenuItem?.parentId ||
              ti.parentId === currentMenuItem.id
          )
          .map((ti) => ({ ...ti, toolTip: ti.toolTip ?? "" }))
      : [];

    const onStopImpersonationClick = async () => {
      const response = await authenticationService.stopImpersonating();

      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    };

    return (
      <>
        <TopBar
          logoImageUrl={logoImageUrl}
          logoAlternativeText={logoAlternativeText}
          userInfo={menu?.userInfo}
          title={t({ id: "facultyPortal" })}
          onStopImpersonationClick={onStopImpersonationClick}
          onMenuClick={() => setCollapsed((prev) => !prev)}
          actions={<ProfileMenu />}
        />
        <Box sx={{ display: "flex" }}>
          {!hideLeftMenu && (
            <>
              <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={!collapsed}
                sx={styles.panelDrawer}
                onClose={() => setCollapsed((prev) => !prev)}
                PaperProps={{ sx: styles.leftPanel }}
              >
                <LeftMenuItemList
                  menuItems={leftMenuItems}
                  currentMenuItem={currentMenuItem}
                  onClick={() => setCollapsed(true)}
                />
                {isMobile && (
                  <ListItem
                    sx={styles.collapseMenu}
                    onClick={() => setCollapsed((prev) => !prev)}
                  >
                    <ListItemIcon>
                      <StartIcon sx={{ transform: "rotate(180deg)" }} />
                    </ListItemIcon>
                    <ListItemText
                      disableTypography
                      primary={t({ id: "collapseMenu" })}
                      title={t({ id: "collapseMenu" })}
                    />
                  </ListItem>
                )}
              </Drawer>
            </>
          )}

          <Container component="main" sx={styles.container}>
            {!mappedParentMenuUrl && <MenuTabs tabs={currentTabs} />}
            {children ?? <Outlet />}
          </Container>
        </Box>
      </>
    );
  }
);

export default LeftMenuLayout;
